import { useQuery } from '@tanstack/react-query'
import { webApi } from '@/services/webApi'
import { normalizeMediaVideo } from '@/lib/mediaUtils'
import type { MediaCategory, MediaVideo } from '@/types/media'

export function useMediaList(params?: {
  category?: string
  platform?: string
  featured?: boolean
  search?: string
  sort?: string
  perPage?: number
}) {
  const query = useQuery({
    queryKey: ['web', 'media', params],
    queryFn: () =>
      webApi.media({
        ...params,
        featured: params?.featured === undefined ? undefined : params.featured ? '1' : '0',
        perPage: params?.perPage ?? 12,
      }),
    staleTime: 5 * 60_000,
  })

  const items: MediaVideo[] = (query.data?.items ?? []).map(normalizeMediaVideo)

  return { ...query, items }
}

export function useMediaFeatured(limit = 12) {
  const query = useQuery({
    queryKey: ['web', 'media', 'featured', limit],
    queryFn: () => webApi.mediaFeatured({ limit }),
    staleTime: 5 * 60_000,
  })

  const items: MediaVideo[] = (query.data ?? []).map(normalizeMediaVideo)

  return { ...query, items }
}

export function useMediaHomepage(limit = 12) {
  const query = useQuery({
    queryKey: ['web', 'media', 'homepage', limit],
    queryFn: () =>
      webApi.media({
        sort: 'order',
        perPage: limit,
      }),
    staleTime: 5 * 60_000,
  })

  const items: MediaVideo[] = (query.data?.items ?? []).map(normalizeMediaVideo)

  return { ...query, items }
}

export function useMediaCategories() {
  const query = useQuery({
    queryKey: ['web', 'media', 'categories'],
    queryFn: () => webApi.mediaCategories(),
    staleTime: 10 * 60_000,
  })

  const categories: MediaCategory[] = (query.data ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    slug: c.slug,
    displayOrder: c.displayOrder,
  }))

  return { ...query, categories }
}

export function useMediaDetail(slug?: string) {
  const query = useQuery({
    queryKey: ['web', 'media', 'detail', slug],
    queryFn: () => webApi.mediaDetail(slug!),
    enabled: Boolean(slug),
    staleTime: 5 * 60_000,
  })

  const video = query.data?.video ? normalizeMediaVideo(query.data.video) : null
  const related = (query.data?.related ?? []).map(normalizeMediaVideo)
  const latest = (query.data?.latest ?? []).map(normalizeMediaVideo)

  return { ...query, video, related, latest }
}
